import isExist from './isExist';

interface ValidateResult {
  result: boolean;
  message?: string;
}

/**
 * @description 通过自定义的校验规则，校验对象属性是否合法
 *
 * @param {object} validateMap 校验规则对象，key-被校验的属性，value-校验规则（数组或函数），规则举例：
 * @one 规则是数组：
 * @rule [ String, true ] - 必须是字符串类型、必须有值；
 * @rule [ String ] - 是字符串类型即可；
 * @rule [ Array, true ]-必须是数组、数组必须有长度；
 * @rule [ String, '值必须是字符串', true, '值不能为空' ] - 若出错的校验项的下一个是字符串，则它可以作为返回的错误详情message；
 * @rule [ String, true, '值必须是非空字符串' ] - 若出错的校验项后面没有错误详情，则会用最后一个；
 * @rule 如果最后没有错误详情，会用默认错误提示：`prop '${首个错误属性}' is invalid`。
 *
 * @two 规则是函数：
 * @rule (value: 被校验属性的值, context: 被校验的整个对象) => true-校验通过、false-校验失败、throw new Error('')-校验失败，错误message是throw Error的值
 *
 * @param {any} validateValue 被校验的对象
 *
 * @returns {ValidateResult} 返回值是一个对象，result: true通过、false失败，message: 具体失败信息
 */
const validate = (
  validateMap: Record<string, any>,
  validateValue: Record<string, any>,
): ValidateResult => {
  if (!isExist(validateValue)) {
    return {
      result: false,
      message: 'error: validateValue is not exist',
    };
  }
  let currentKey = '',
    currentRules: any[] = [],
    currentIndex = 0,
    throwError: Error | null = null;
  try {
    const isValid = Object.entries(validateMap).some(([key, rules]) => {
      currentKey = key;
      currentRules = rules as any[];
      if (Array.isArray(rules)) {
        return rules.some((rule, index) => {
          currentIndex = index;
          // 字符串是描述错误详情的，遇到跳过
          if (typeof rule === 'string') {
            return false;
          }
          if (validateValue[key] === undefined || validateValue[key] === null) {
            return true;
          }
          if (typeof rule === 'function') {
            return validateValue[key].constructor !== rule;
          } else if (rule === true) {
            const value = validateValue[key];
            if (Array.isArray(value)) {
              return value.length === 0;
            } else if (typeof value === 'number' && isNaN(value)) {
              return true;
            } else {
              return !value;
            }
          }
        });
      } else if (typeof rules === 'function') {
        try {
          return !(rules as any)(validateValue[key], validateValue);
        } catch (error) {
          throwError = error;
          return true;
        }
      } else {
        return true;
      }
    });
    if (isValid) {
      // 优先看是否有函数抛出的错误，然后看本条规则的下一个是否是错误信息，不是则看最后一个
      if (throwError) {
        throw throwError;
      }
      const message =
        typeof currentRules[currentIndex + 1] === 'string'
          ? currentRules[currentIndex + 1]
          : currentRules[currentRules.length - 1];
      if (typeof message === 'string') {
        throw new Error(message);
      }
      throw new Error(`prop '${currentKey}' is invalid`);
    } else {
      return {
        result: true,
      };
    }
  } catch (e) {
    return {
      result: false,
      message: `${e}`,
    };
  }
};

export default validate;
